import React, { useEffect, useMemo, useState } from 'react';
import { useListRefreshListener } from "../../utils/listNavigation";
import moment from 'moment';
import { useLocation, useNavigate } from 'react-router-dom';
import SmartTable from '../../components/common/table-component';
import DashboardHeader from '../../components/header/DashboardHeader';
import MobileMenu from '../../components/header/MobileMenu';
import DashboardEmployerSidebar from '../../components/header/DashboardSidebar';
import CopyrightFooter from '../../components/footer/employers/CopyrightFooter';
import { t } from 'i18next';
import insuranceType from "../../data/insuranceType"
import DatePicker from 'react-datepicker';
import { callSearchAgentInfo } from '../../api/settings/service';
import { useDispatch, useSelector } from 'react-redux';
import DataAutoComplete from "../../components/auto-complete";
import { callApproveCommission, callGetCommissionDetails, callGetCommissionPayout, callGetContractByKey } from '../../api/contract/service';
import colors from '../../utils/colors';
import PermissionWrapper from '../../components/common/PermissionWrapper';
import { APPROVE, CREATE, EDIT } from '../../utils/commonType';
import SwalAlert from '../../components/common/alert';
import { convertStringToJson, encodeBase64 } from '../../utils/commonUtil';
import { DocumentPreviewUI } from '../../components/document-preview';
import './commission-ui-only.css';
import { findLoggedAgent, getCommissionAgentScope } from './commissionAgentScope';


const CommissionPayoutHistoryPage = () => {
    const menu = useSelector((state) => state.menuData);
    const userInfo = useSelector((state) => state.dataLogin);
    const agentScope = getCommissionAgentScope(userInfo);
    const isAgentUser = agentScope.isAgentUser;
    const loggedAgentId = agentScope.agentId;
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [agentData, setAgentData] = useState(null);
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
    const [resetSelectedKey, setResetSelectedKey] = useState(0);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [request, setRequest] = useState({ page: 1, maxResult: 10, keyword: '', startDate: startOfMonth, endDate: new Date(), insuranceType: 1, agentId: isAgentUser ? loggedAgentId : "" });

    const [agents, setAgents] = useState([]);
    const [agent, setAgent] = useState([]);

    const [filters, setFilters] = useState({
        agent_code: '',
        status: '0', // 0=earned, 1=approved
        from_date: '',
        to_date: '',
        keyword: '',
    });

    const [data, setData] = useState({
        result: [],
        totalRecords: 0,
        totalPages: 1,
        sumRecords: 0,
    });

    const [selectedIds, setSelectedIds] = useState([]);

    useEffect(() => {
        const initializeAgentScope = async () => {
            if (isAgentUser) {
                if (!loggedAgentId) {
                    setData({ result: [], totalRecords: 0, totalPages: 1, sumRecords: 0 });
                    return;
                }

                const lockedAgent = await findLoggedAgent({
                    dispatch,
                    callSearchAgentInfo,
                    scope: agentScope,
                });

                if (lockedAgent) {
                    setAgentData(lockedAgent);
                    setAgent([lockedAgent]);
                }

                setRequest((prev) => ({
                    ...prev,
                    page: 1,
                    agentId: loggedAgentId,
                }));

                await loadDataCom({ page: 1, agentId: loggedAgentId });
                return;
            }

            setAgentData(null);
            await loadDataCom({ page: 1 });
        };

        initializeAgentScope();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname, isAgentUser, loggedAgentId, agentScope.agentCode]);
    const selectedRows = useMemo(() => {
        const rowMap = new Map((data?.result || []).map((item) => [item.id, item]));
        return selectedIds.map((id) => rowMap.get(id)).filter(Boolean);
    }, [selectedIds, data]);

    const summary = useMemo(() => {
        return selectedRows.reduce(
            (acc, item) => {
                acc.totalItems += 1;
                acc.totalCommission += Number(item.commission_amount || 0);
                acc.totalTax += Number(item.com_tax || 0);
                acc.netCommission += Number(item.net_commission || 0);
                return acc;
            },
            {
                totalItems: 0,
                totalCommission: 0,
                totalTax: 0,
                netCommission: 0,
            }
        );
    }, [selectedRows]);

    const columns = [
        {
            label: 'agentFullName',
            key: 'agentFullName',
            style: { minWidth: 180 },
        },
        {
            label: 'agentCode',
            key: 'agentCode',
            style: { minWidth: 100 },
        },
        {
            label: 'payoutNo',
            key: 'payoutNo',
            style: { minWidth: 180 },
        },
        {
            label: 'totalCommission',
            key: 'totalCommission',
            className: 'text-end',
            style: { minWidth: 120 },
            render: (value) => (<span style={{ color: colors.primary, fontWeight: 700 }}>
                {Number(value || 0).toLocaleString()}
            </span>),
        },
        {
            label: 'companyReceiveTotal',
            key: 'companyReceiveTotal',
            className: 'text-end',
            style: { minWidth: 120 },
            render: (value) => (
                <span style={{ color: colors.primary, fontWeight: 700 }}>
                    {Number(value || 0).toLocaleString()}
                </span>
            ),
        },
        {
            label: 'paidDate',
            key: 'paidDate',
            style: { minWidth: 150 },
            render: (value) => value ? moment(value).utc().format('DD/MM/YYYY HH:mm') : '-',
        },
        {
            label: 'reversedReason',
            key: 'reversedReason',
            style: { minWidth: 180 },
        },
        , {
            label: 'slipPath', key: 'slipPath', render: (val) => {
                try {
                    return val && <DocumentPreviewUI documents={convertStringToJson(val)} variant="cell" />
                } catch (error) {
                    return ''
                }
            }

        },
        {
            label: 'status',
            key: 'status',
            style: { minWidth: 120 },
            render: (value) => {
                if (Number(value) === 1) {
                    return <span className="badge bg-secondary">{t("status.inPayout")}</span>;
                }
                if (Number(value) === 2) {
                    return <span className="badge bg-success">{t("status.paid")}</span>;
                }
                if (Number(value) === 3) {
                    return <span className="badge bg-danger">{t("status.cancelled")}</span>;
                }
                return <span className="badge bg-light text-dark">{value}</span>;
            },
        },
    ];
    const handleApprove = async (item) => {
        navigate(pathname + "/approved/" + encodeBase64(JSON.stringify({ ...item, view: true })))
    }

    const handleChange = (e) => {
        const { name, value } = e.target;
        setRequest((prev) => ({ ...prev, [name]: value }));
    };

    const onSearch = async (e) => {
        e.preventDefault()
        loadDataCom()
    }
    const loadDataCom = async (e) => {
        if (isAgentUser && !loggedAgentId) return;

        let data = {
            ...request,
            ...(isAgentUser ? { agentId: loggedAgentId } : {}),
        };
        if (e) {
            data = {
                ...data,
                ...e,
                ...(isAgentUser ? { agentId: loggedAgentId } : {}),
            }
        }
        const rsp = await dispatch(callGetCommissionPayout(data));
        if (rsp?.payload?.responseCode === "00000") {
            setData(rsp.payload.data)

        }

    }
    const loadData = async (value) => {
        if (isAgentUser) {
            if (!loggedAgentId) return;
            const lockedAgent = await findLoggedAgent({
                dispatch,
                callSearchAgentInfo,
                scope: agentScope,
            });
            if (lockedAgent) {
                setAgentData(lockedAgent);
                setAgent([lockedAgent]);
            }
            return;
        }

        if (value?.id) {
            let data = { keyword: null, page: 1, maxResult: 1, agentId: value?.id, type: 1 };
            const rsp = await dispatch(callSearchAgentInfo(data));
            if (rsp?.payload?.responseCode === "00000" && rsp?.payload?.data?.result.length >= 1) {
                setAgent(rsp.payload.data.result)
            }

        } else {
            let data = { keyword: value, page: 1, maxResult: 10, agentId: null, type: 1 };
            const rsp = await dispatch(callSearchAgentInfo(data));
            if (rsp?.payload?.responseCode === "00000" && rsp?.payload?.data?.result.length >= 1) {
                setAgent(rsp.payload.data.result)
            }
        }
    };

    const onSelect = async (item) => {
        if (isAgentUser) {
            setRequest((prev) => ({
                ...prev,
                agentId: loggedAgentId,
            }));
            return;
        }

        if (item) {
            setRequest((prev) => ({
                ...prev,
                agentId: item.agentId
            }));
        } else {
            setRequest((prev) => ({
                ...prev,
                agentId: null
            }));
        }

    }
    const handleEdit = (item) => {
        navigate(pathname + "/save/" + encodeBase64(JSON.stringify(item)))
    }
    const handleView = (item) => {
        navigate(pathname + "/view/" + encodeBase64(JSON.stringify(item)))
    }
    useListRefreshListener(loadDataCom);

    return (
        <div className="page-wrapper dashboard commission-ui-only">
            <span className="header-span"></span>
            <DashboardHeader />
            <MobileMenu />
            <DashboardEmployerSidebar />
            <section className="user-dashboard">
                <div className="dashboard-outer">
                    <div className="upper-title-box commission-page-title">
                        <h4>{t("menu.commission.commissionPayoutHistory")}</h4>
                        <div className="group-button-header">
                            {
                                <PermissionWrapper permission={CREATE}>
                                    <button type="button" className="btn btn-primary" onClick={() => navigate(pathname + '/save')}>
                                        <i className="fas fa-plus"></i> {t("button.add")}
                                    </button>
                                </PermissionWrapper>

                            }
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-12">
                            <div className="ls-widget commission-card">
                                <div className="tabs-box">
                                    <div className="widget-content p-4">
                                        <div className="default-form commission-form">
                                            <div className="row">
                                                <div className="col-md-3 form-group">
                                                    <label> {t('contracts.life.agentCode')}<span className="text-danger">*</span></label>
                                                    <DataAutoComplete
                                                        disabled={isAgentUser}
                                                        label="firstName"
                                                        id="agentCode"
                                                        initialData={agentData}
                                                        data={agent}
                                                        autoSelect={true}
                                                        placeholder={t('contract.keyword')}
                                                        handleChange={loadData}
                                                        onSelect={onSelect}
                                                    />
                                                </div>
                                                <div className="col-md-3 form-group">
                                                    <label>{t('startDate')}</label>
                                                    <DatePicker
                                                        selected={request.startDate}
                                                        onChange={date => setRequest((prev) => ({
                                                            ...prev,
                                                            startDate: moment(date).format('YYYY-MM-DD'),
                                                        }))}
                                                        dateFormat="dd/MM/yyyy"
                                                        placeholderText={t('startDate')}
                                                        className="form-control-uld"
                                                    />
                                                </div>

                                                <div className="col-md-3 form-group">
                                                    <label>{t('endDate')}</label>
                                                    <DatePicker
                                                        selected={request.endDate}
                                                        onChange={date => setRequest((prev) => ({
                                                            ...prev,
                                                            endDate: moment(date).format('YYYY-MM-DD'),
                                                        }))}
                                                        dateFormat="dd/MM/yyyy"
                                                        placeholderText={t('endDate')}
                                                        className="form-control-uld"
                                                    />
                                                </div>
                                                {/* <div className="col-md-2 form-group">
                                                    <label>{t('callcenter.payment.insuranceType')}</label>
                                                    <select className="chosen-single form-select"
                                                        onChange={handleChange}
                                                        name="insuranceType"
                                                        value={request.insuranceType}
                                                    >
                                                        {
                                                            insuranceType.map((item) => (
                                                                <option key={item.id} value={item.id}>{t(item.name)}</option>
                                                            ))
                                                        }
                                                    </select>
                                                </div> */}
                                                <div className="col-md-3 form-group">
                                                    <label>&nbsp;</label>
                                                    <button className="theme-btn btn-style-one w-100 commission-search-btn" onClick={onSearch}>
                                                        <i className="fas fa-search"></i>&nbsp;{t("button.search")}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-12">
                            <div className="ls-widget commission-card">
                                <div className="tabs-box">
                                    <div className="widget-content p-4">
                                        <SmartTable
                                            resetSelectedKey={resetSelectedKey}
                                            columns={columns}
                                            data={data}
                                            onLazyLoad={(params) => loadDataCom(params)}
                                            recordsTableFilter={true}
                                            customPagination={true}
                                            actions={(item) => (
                                                <div className="d-flex align-items-center gap-1" style={{ minWidth: 145 }}>
                                                    <button
                                                        type="button"
                                                        className="btn btn-primary btn-sm commission-action-btn"
                                                        title={t('commission.view')}
                                                        onClick={() => handleView(item)}
                                                    >
                                                        <i className="fas fa-eye"></i>
                                                    </button>
                                                    <PermissionWrapper permission={APPROVE}>
                                                        <button disabled={item.status != 1} className="btn btn-success btn-sm commission-action-btn" onClick={() => handleApprove(item)}>
                                                            <i className="fas fa-check"></i>
                                                        </button>
                                                    </PermissionWrapper>
                                                    <PermissionWrapper permission={EDIT}>
                                                        <button disabled={item.status != 1} className="btn btn-warning btn-sm commission-action-btn" onClick={() => handleEdit(item)}>
                                                            <i className="fas fa-edit"></i>
                                                        </button>
                                                    </PermissionWrapper>
                                                </div>
                                            )}
                                        />

                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section >
            <CopyrightFooter />
        </div >

    );
};

export default CommissionPayoutHistoryPage;